import {
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  FilterIcon,
  TrashIcon,
  UndoIcon,
  ZoomInIcon,
} from '@iconicicons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deepEqual } from '../../utils/objectUtils';
import { useClientPagination, useDebouncedCallback } from '../../helpers/hooks';
import { useSelector } from 'react-redux';
import { selectAllNodes, selectAllPoints } from '../../map/slice/mapSelectors';
import { InfiniteScrollObserver } from '../../helpers/InfiniteScrollObserver';
import { numberFormatter } from '../../helpers/formatters';
import { useFocusPoint } from '../../hooks/useFocusPoint';
import { useGetTSPPrioritiesQuery } from '../../services/edgeApi';
import { BooleanStatus } from '../../helpers/BooleanStatus';

/**
 * @typedef {{
 *  pointConstraints: import('@/types').PointConstraint[];
 *  deletedPointConstraints: import('@/types').PointConstraint[];
 *  onDelete: (row: import('@/types').PointConstraint) => void;
 *  onBulkDelete: (constraintIds: number[]) => void;
 *  onRestore: (ids: string[]) => void;
 *  onBulkUpdate: (ids: string[], patch: Partial<import('@/types').PointConstraint>) => void;
 *  isEditing: boolean;
 *  onChange: (constraint: import('@/types').PointConstraint) => void;
 * }}  PointConstraintTableProps
 */

/**
 * @param { PointConstraintTableProps} props
 * @returns {React.FC< PointConstraintTableProps>}
 */
export const PointConstraintTable = ({
  pointConstraints,
  deletedPointConstraints,
  isEditing,
  onDelete,
  onBulkDelete,
  onRestore,
  onBulkUpdate,
  onChange: _onChange,
}) => {
  const focusPoint = useFocusPoint();
  const allPoints = useSelector(selectAllPoints);
  const allNodes = useSelector(selectAllNodes);
  const [filters, setFilters] = useState({
    point: '',
    pipeline: '',
    flow_direction: '',
    description: '',
    constraint_factor: '',
    max_volume: '',
    cuts_at_priority: '',
  });

  const { data: priorities } = useGetTSPPrioritiesQuery();

  /**
   * @type {import('@/types/types').State<{[key: string]: boolean | undefined}>}
   */
  const [selectedConstraints, setSelectedConstraints] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);

  /**
   *
   * @param {import('../scenarioSlice').PointConstraint} constraint
   * @param {boolean} selected
   */
  const onSelect = (constraint, selected) => {
    setSelectedConstraints((p) => ({
      ...p,
      [constraint.component_key]: selected,
    }));
  };

  const onChange = useCallback(
    /**
     *
     * @param {import('../scenarioSlice').PointConstraint} constraint
     * @param {Partial<import('../scenarioSlice').PointConstraint>} patch
     */
    (constraint, patch) => {
      _onChange({ ...constraint, ...patch });
    },
    [_onChange],
  );

  /**
   *
   * @param {import('../../services/edgeApi').Point?} point
   */
  const onLocateConstraint = (point) => {
    if (point) {
      focusPoint(point.id);
    }
  };

  const onChangeFilter = useDebouncedCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  });

  const filteredConstraints = useMemo(() => {
    return pointConstraints.filter((constraint) => {
      const point = allPoints[constraint.point];
      if (!point) return false;
      const location = `${point.id} ${point.name}`;
      const data = { ...constraint, location };
      return Object.entries(filters)
        .filter(([_key, value]) => {
          if (value === null || value === undefined) {
            return true;
          }
          if (typeof value === 'string') {
            return value.trim().length > 0;
          }
          return true;
        })
        .map(([key, value]) => {
          return [key, String(value).trim().toLowerCase()];
        })
        .every(([key, value]) => {
          return String(data[key]).trim().toLowerCase().includes(value);
        });
    });
  }, [pointConstraints, filters, allPoints]);

  const {
    slice: paginatedFilteredConstraints,
    exhausted,
    getNext,
    reset,
  } = useClientPagination(filteredConstraints, 30);

  useEffect(() => reset(), [filteredConstraints]);

  const isEmptySelection = useMemo(
    () => Object.values(selectedConstraints).every((selected) => !selected),
    [selectedConstraints],
  );

  if (pointConstraints.length === 0) {
    return (
      <div
        style={{
          minHeight: '200px',
          height: '100%',
        }}
        className="d-flex align-items-center justify-content-center"
      >
        <p className="text-center text-muted">No point constraints</p>
      </div>
    );
  }
  return (
    <table className="table">
      <thead
        style={{
          position: 'sticky',
          top: 0,
        }}
      >
        <tr>
          <th>
            Point
            <div className="d-flex align-items-center mt-2">
              <FilterIcon className="mx-3" />
              <input
                className="form-control"
                type="text"
                onChange={(evt) => onChangeFilter('location', evt.target.value)}
              />
            </div>
          </th>
          <th>
            TSP
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) => onChangeFilter('pipeline', evt.target.value)}
              />
            </div>
          </th>
          <th>
            Direction
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) =>
                  onChangeFilter('flow_direction', evt.target.value)
                }
              />
            </div>
          </th>
          <th>
            Description
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) =>
                  onChangeFilter('description', evt.target.value)
                }
              />
            </div>
          </th>
          <th>
            Constraint Factor (%)
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="number"
                onChange={(evt) =>
                  onChangeFilter('constraint_factor', evt.target.value)
                }
              />
            </div>
          </th>
          <th>
            Max Volume
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="number"
                onChange={(evt) =>
                  onChangeFilter('max_volume', evt.target.value)
                }
              />
            </div>
          </th>
          <th style={{ minWidth: '100px' }}>
            Cuts at Priority
            <div className="d-flex align-items-center mt-2">
              <input
                className="form-control"
                type="text"
                onChange={(evt) =>
                  onChangeFilter('cuts_at_priority', evt.target.value)
                }
              />
            </div>
          </th>
          <th>Cuts All Below</th>
          {isEditing && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {isEditing && (
          <>
            {showDeleted && deletedPointConstraints.length > 0 && (
              <tr>
                <td colSpan={10}>
                  <div
                    onClick={() =>
                      onRestore(deletedPointConstraints.map((m) => m.id))
                    }
                    role="button"
                    className="d-flex align-items-center justify-content-center text-success"
                  >
                    <span>
                      Restore all <UndoIcon />
                    </span>
                  </div>
                </td>
              </tr>
            )}
            {showDeleted &&
              deletedPointConstraints.map((constraint) => {
                const point = allPoints[constraint.point];
                const node = allNodes[point?.node];

                return (
                  <DeletedPointConstraintTableRow
                    key={constraint.id}
                    constraint={constraint}
                    point={point}
                    node={node}
                    onLocateConstraint={onLocateConstraint}
                    onRestore={onRestore}
                  />
                );
              })}
            {deletedPointConstraints.length > 0 && (
              <tr>
                <td colSpan={10}>
                  <div
                    onClick={() => setShowDeleted((p) => !p)}
                    role="button"
                    className="d-flex flex-column align-items-center justify-content-center"
                  >
                    <span>
                      {showDeleted ? 'Hide' : 'Show'}{' '}
                      {deletedPointConstraints.length} deleted constraints
                    </span>
                    <span>
                      {showDeleted ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </span>
                  </div>
                </td>
              </tr>
            )}
            <BulkEditPointConstraintTableRow
              onSelectAll={(checked) => {
                const selected = filteredConstraints.reduce(
                  (acc, constraint) => {
                    acc[constraint.component_key] = checked;
                    return acc;
                  },
                  {},
                );
                setSelectedConstraints(selected);
              }}
              allChecked={filteredConstraints.every(
                (constraint) => selectedConstraints[constraint.component_key],
              )}
              noneChecked={isEmptySelection}
              onDeleteSelected={() => {
                const selected = filteredConstraints
                  .filter(
                    (constraint) =>
                      selectedConstraints[constraint.component_key],
                  )
                  .map((constraint) => constraint.id);
                if (selected.length > 0) {
                  onBulkDelete(selected);
                  setSelectedConstraints({});
                }
              }}
              onBulkUpdate={(patch) => {
                const selection = filteredConstraints
                  .filter(
                    (constraint) =>
                      selectedConstraints[constraint.component_key],
                  )
                  .map((constraint) => constraint.id);
                onBulkUpdate(selection, patch);
              }}
            />
          </>
        )}
        {paginatedFilteredConstraints.map((constraint) => {
          const point = allPoints[constraint.point];
          const node = allNodes[point?.node];
          return isEditing ? (
            <EditPointConstraintTableRow
              key={constraint.component_key}
              constraint={constraint}
              point={point}
              node={node}
              onDelete={onDelete}
              onChange={onChange}
              onLocateConstraint={onLocateConstraint}
              onSelect={onSelect}
              isSelected={Boolean(
                selectedConstraints[constraint.component_key],
              )}
              priorities={priorities}
            />
          ) : (
            <PointConstraintTableRow
              key={constraint.component_key}
              constraint={constraint}
              point={point}
              node={node}
              onLocateConstraint={onLocateConstraint}
            />
          );
        })}
        <tr>
          <td colSpan={isEditing ? 10 : 9}>
            <InfiniteScrollObserver
              onScrollIn={() => !exhausted && getNext()}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 *
 * @param {{
 *  constraint: import('@/types').PointConstraint & {pipeline: string; name: string};
 *  point: import('@/types').Point;
 *  node: import('@/types').Node;
 *  onLocateConstraint: (point: import('@/types').Point) => void;
 * }} props
 * @returns
 */
const _PointConstraintTableRow = ({
  constraint,
  point,
  node,
  onLocateConstraint,
}) => {
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-light me-2"
            disabled={!node}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateConstraint(point)}
          >
            <ZoomInIcon />
          </button>
          <span>
            <strong className="me-2">#{constraint.point}</strong> {point.name}
          </span>
        </div>
      </td>
      <td>{constraint.pipeline}</td>
      <td>{constraint.flow_direction}</td>
      <td>{constraint.description}</td>
      <td className="text-center">{constraint.constraint_factor * 100}%</td>
      <td className="text-center">{numberFormatter(constraint.max_volume)}</td>
      <td className="text-center">{constraint.cuts_at_priority}</td>
      <td className="text-center">
        <BooleanStatus value={constraint.cuts_all_below} />
      </td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  pointConstraint: import('@/types').PointConstraint & {pipeline: string;};
 *  point?: import('@/types').Point;
 *  node: import('@/types').Node;
 *  onLocateConstraint: (point: import('@/types').Point) => void;
 *  onRestore: (ids: string[]) => void;
 * }} props
 * @returns
 */
const _DeletedPointConstraintTableRow = ({
  constraint,
  point,
  node,
  onLocateConstraint,
  onRestore,
}) => {
  return (
    <tr className="table-danger">
      <td>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-light me-2"
            disabled={!node}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateConstraint(point)}
          >
            <ZoomInIcon />
          </button>
          <span>
            <strong className="me-2">#{constraint.point}</strong> {point.name}
          </span>
        </div>
      </td>
      <td>{constraint.pipeline}</td>
      <td>{constraint.flow_direction}</td>
      <td>{constraint.description}</td>
      <td className="text-center">{constraint.constraint_factor * 100}%</td>
      <td className="text-center">{numberFormatter(constraint.max_volume)}</td>
      <td className="text-center">{constraint.cuts_at_priority}</td>
      <td className="text-center">
        <BooleanStatus value={constraint.cuts_all_below} />
      </td>
      <td
        className="text-center"
        role="button"
        onClick={() => onRestore([constraint.id])}
      >
        <UndoIcon color="green" />
      </td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  constraint: import('@/types').PointConstraint & {pipeline: string; name: string};
 *  point: import('@/types').Point;
 *  node: import('@/types').Node;
 *  onDelete: (row: import('@/types').PointConstraint) => void;
 *  onChange: (original: import('@/types').PointConstraint, edited: import('@/types').PointConstraint) => void;
 *  onLocateConstraint: (point: import('@/types').Point) => void;
 *  onSelect: (constraint: import('@/types').PointConstraint, selected: boolean) => void;
 *  isSelected: boolean;
 *  priorities: Record<string, import('@/types/api/TSPPriority').TSPPriority>;
 * }} props
 * @returns
 */
const _EditPointConstraintTableRow = ({
  constraint,
  point,
  node,
  onLocateConstraint,
  onDelete,
  onChange,
  onSelect,
  isSelected,
  priorities,
}) => {
  return (
    <tr className={isSelected ? 'table-secondary' : ''}>
      <td>
        <div className="d-flex align-items-center">
          <div
            style={{ width: '30px' }}
            role="button"
            onClick={() => onSelect(constraint, !isSelected)}
          >
            <input
              type="checkbox"
              readOnly
              checked={isSelected}
              className="form-check-input form-check-input-sm me-2"
            />
          </div>
          <button
            className="btn btn-light me-2"
            disabled={!node}
            style={{
              opacity: 1,
            }}
            onClick={() => onLocateConstraint(point)}
          >
            <ZoomInIcon />
          </button>
          <span onClick={() => onSelect(constraint, !isSelected)} role="button">
            <strong className="me-2">#{constraint.point}</strong> {point.name}
          </span>
        </div>
      </td>
      <td onClick={() => onSelect(constraint, !isSelected)} role="button">
        {constraint.pipeline}
      </td>
      <td>
        <select
          value={constraint.flow_direction}
          className="form-select form-select-sm"
          onChange={(e) =>
            onChange(constraint, { flow_direction: e.target.value })
          }
        >
          <option value="placeholder" disabled>
            Select Direction
          </option>
          <option value="RECEIPT">Receipt</option>
          <option value="DELIVERY">Delivery</option>
        </select>
      </td>
      <td>
        <textarea
          type="text"
          className="form-control form-control-sm"
          value={constraint.description}
          onChange={(e) =>
            onChange(constraint, { description: e.target.value })
          }
        />
      </td>
      <td className="text-center">
        <input
          type="number"
          className="form-control form-control-sm"
          value={constraint.constraint_factor * 100}
          onChange={(e) =>
            onChange(constraint, {
              constraint_factor: e.target.value / 100,
            })
          }
          min={0}
          max={100}
        />
      </td>
      <td className="text-center">
        <input
          type="number"
          className="form-control form-control-sm"
          value={constraint.max_volume}
          onChange={(e) => onChange(constraint, { max_volume: e.target.value })}
          min={0}
        />
      </td>
      <td className="text-center">
        <select
          type="select"
          className="form-select form-select-sm"
          value={constraint.cuts_at_priority}
          onChange={(e) =>
            onChange(constraint, { cuts_at_priority: e.target.value })
          }
        >
          <option value="placeholder" disabled>
            Select Priority
          </option>
          {priorities?.[point?.tsp_id]?.point.map((priority) => (
            <option value={priority} key={priority}>
              {priority}
            </option>
          ))}
        </select>
      </td>
      <td className="text-center">
        <label
          className="form-label"
          htmlFor={`cuts_all_below_${constraint.point}`}
        >
          <BooleanStatus value={constraint.cuts_all_below} />
        </label>
        <div className="form-switch">
          <input
            type="checkbox"
            id={`cuts_all_below_${constraint.point}`}
            className="form-check-input"
            checked={constraint.cuts_all_below}
            onChange={(e) =>
              onChange(constraint, { cuts_all_below: e.target.checked })
            }
          />
        </div>
      </td>
      <td className="text-center">
        <TrashIcon
          onClick={() => onDelete(constraint)}
          role="button"
          color="red"
        />
      </td>
    </tr>
  );
};

/**
 *
 * @param {{
 *  allChecked: boolean;
 *  noneChecked: boolean;
 *  onSelectAll: (selected: boolean) => void;
 *  onDeleteSelected: () => void;
 *  onBulkUpdate: (patch: Partial<import('../scenarioSlice').PointConstraint>) => void;
 * }} props
 * @returns
 */
const BulkEditPointConstraintTableRow = ({
  allChecked,
  noneChecked,
  onSelectAll,
  onDeleteSelected,
  onBulkUpdate,
}) => {
  /**
   * @type {import('../../types/types').State<Partial<import('../scenarioSlice').PointConstraint>}
   */
  const [patch, setPatch] = useState({
    flow_direction: '',
    description: '',
    constraint_factor: 0,
    max_volume: 0,
    cuts_at_priority: '',
  });

  const reset = () => {
    setPatch({
      flow_direction: '',
      description: '',
      constraint_factor: 0,
      max_volume: 0,
      cuts_at_priority: '',
    });
  };

  useEffect(() => {
    if (noneChecked) {
      reset();
    }
  }, [noneChecked]);

  return (
    <tr>
      <td colSpan={2}>
        <div className="d-flex align-items-center">
          <input
            type="checkbox"
            readOnly
            checked={allChecked}
            onChange={(e) => onSelectAll(e.target.checked)}
            className="form-check-input form-check-input-sm me-3"
          />
          <button
            className={`btn ${noneChecked ? 'btn-secondary' : 'btn-danger'}`}
            style={{ opacity: 1 }}
            disabled={noneChecked}
            onClick={onDeleteSelected}
          >
            Delete Selected
          </button>
        </div>
      </td>
      {noneChecked ? (
        <td colSpan={7}></td>
      ) : (
        <>
          <td>
            <select
              value={patch.flow_direction}
              className="form-select form-select-sm"
              onChange={(e) =>
                setPatch((p) => ({ ...p, flow_direction: e.target.value }))
              }
            >
              <option value="placeholder" disabled>
                Select Direction
              </option>
              <option value="RECEIPT">Receipt</option>
              <option value="DELIVERY">Delivery</option>
            </select>
          </td>
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={patch.description}
              onChange={(e) =>
                setPatch((p) => ({ ...p, description: e.target.value }))
              }
            />
          </td>
          <td className="text-center">
            <input
              type="number"
              className="form-control form-control-sm"
              value={patch.constraint_factor * 100}
              onChange={(e) =>
                setPatch((p) => ({
                  ...p,
                  constraint_factor: e.target.value / 100,
                }))
              }
              min={0}
              max={100}
            />
          </td>
          <td className="text-center">
            <input
              type="number"
              className="form-control form-control-sm"
              value={patch.max_volume}
              onChange={(e) =>
                setPatch((p) => ({ ...p, max_volume: e.target.value }))
              }
              min={0}
            />
          </td>
          <td className="text-center">
            <select
              type="select"
              className="form-select form-select-sm"
              disabled
            >
              <option value="placeholder" disabled></option>
            </select>
          </td>
          <td className="text-center">
            <label className="form-label" htmlFor={`cuts_all_below_edit`}>
              <BooleanStatus value={patch.cuts_all_below} />
            </label>
            <div className="form-switch">
              <input
                type="checkbox"
                id={`cuts_all_below_edit`}
                className="form-check-input"
                checked={patch.cuts_all_below}
                onChange={(e) =>
                  setPatch((p) => ({ ...p, cuts_all_below: e.target.checked }))
                }
              />
            </div>
          </td>
          <td className="text-center">
            <EditIcon
              className="text-primary"
              onClick={() => {
                onBulkUpdate(
                  Object.fromEntries(
                    Object.entries(patch).filter(([_, v]) => Boolean(v)),
                  ),
                );
                reset();
              }}
              role="button"
            />
          </td>
        </>
      )}
    </tr>
  );
};

const PointConstraintTableRow = React.memo(
  _PointConstraintTableRow,
  (prev, next) => {
    return deepEqual(prev.constraint, next.constraint);
  },
);

const DeletedPointConstraintTableRow = React.memo(
  _DeletedPointConstraintTableRow,
  (prev, next) => {
    return deepEqual(prev.constraint, next.constraint);
  },
);

const EditPointConstraintTableRow = React.memo(
  _EditPointConstraintTableRow,
  (prev, next) => {
    return (
      deepEqual(prev.constraint, next.constraint) &&
      deepEqual(prev.priorities, next.priorities) &&
      prev.isSelected === next.isSelected
    );
  },
);
